import { defineComponent } from "vue";
import BpmnViewer from "bpmn-js/lib/NavigatedViewer";
import { QuizInitialProcess } from "@/interfaces/sdk-quiz";
import {
  loadAllProcessesByEnvironment,
  loadInitialProcessConfig,
  loadXmlFromCamunda,
  saveInitialProcess,
  updateInitialProcess,
  removeInitialProcess,
} from "@/store/quiz/actions";
import { EventBus } from "@/utils/event-bus";
import { ETypeNotification } from "@/interfaces/i-notification";

export default defineComponent({
  name: "sdk-quiz-start-process",
  data() {
    return {
      processKey: "",
      initialProcessConfig: {} as QuizInitialProcess,
      xml: "",
      bpmnViewer: {} as any,
      processesList: [] as any[],
      saveAction: "save" as "save" | "update",
      loading: true,
      loadingXML: false,
    };
  },
  methods: {
    togglePageLoading() {
      this.loading = !this.loading;
    },
    renderXml() {
      if (!Object.keys(this.bpmnViewer).length) {
        this.bpmnViewer = new BpmnViewer({
          container: "#bpmn-container",
        });
      }
      this.bpmnViewer.importXML(this.xml, async (err: any) => {
        if (!err) {
          const canvas = await this.bpmnViewer.get("canvas");
          canvas.zoom("fit-viewport");
        }
      });
    },
    getXmlFromEngine() {
      this.loadingXML = true;
      this.$store
        .dispatch(loadXmlFromCamunda(this.processKey))
        .then((data) => {
          this.xml = data.bpmn20Xml;
          this.renderXml();
        })
        .catch((err) => {
          EventBus.$emit("emmit-notification", {
            type: ETypeNotification.DANGER,
            title: this.$t("quiz.startConfig.notifications.loadXmlError"),
            message: err[0],
          });
        })
        .finally(() => {
          this.loadingXML = false;
        });
    },
    navigateHome() {
      this.$router.push({ name: "dashboard" });
    },
    saveProcessHandler() {
      if (this.saveAction === "save") {
        this.saveProcess();
      } else {
        this.updateProcess();
      }
    },
    saveProcess() {
      this.togglePageLoading();
      this.$store
        .dispatch(saveInitialProcess(this.processKey))
        .then(async () => {
          await this.getCurrentConfig();
          EventBus.$emit("emmit-notification", {
            type: ETypeNotification.SUCCESS,
            title: this.$t("quiz.startConfig.notifications.saveInitialProcessSuccess"),
          });
        })
        .catch((err) => {
          EventBus.$emit("emmit-notification", {
            type: ETypeNotification.DANGER,
            title: this.$t("quiz.startConfig.notifications.saveInitialProcessError"),
            message: err[0],
          });
        })
        .finally(() => {
          this.togglePageLoading();
        });
    },
    updateProcess() {
      this.togglePageLoading();
      this.$store
        .dispatch(updateInitialProcess(this.initialProcessConfig.id, this.processKey))
        .then(async () => {
          await this.getCurrentConfig();
          EventBus.$emit("emmit-notification", {
            type: ETypeNotification.SUCCESS,
            title: this.$t("quiz.startConfig.notifications.saveInitialProcessSuccess"),
          });
        })
        .catch((err) => {
          EventBus.$emit("emmit-notification", {
            type: ETypeNotification.DANGER,
            title: this.$t("quiz.startConfig.notifications.saveInitialProcessError"),
            message: err[0],
          });
        })
        .finally(() => {
          this.togglePageLoading();
        });
    },
    removeProcess() {
      this.togglePageLoading();
      this.$store
        .dispatch(removeInitialProcess())
        .then(() => {
          this.saveAction = "save";
          this.initialProcessConfig = Object.assign({} as QuizInitialProcess, {});
          this.processKey = "";
          this.xml = "";
          EventBus.$emit("emmit-notification", {
            type: ETypeNotification.SUCCESS,
            title: this.$t("quiz.startConfig.notifications.removeInitialProcessSuccess"),
          });
        })
        .catch((err) => {
          EventBus.$emit("emmit-notification", {
            type: ETypeNotification.DANGER,
            title: this.$t("quiz.startConfig.notifications.removeInitialProcessError"),
            message: err[0],
          });
        })
        .finally(() => {
          this.togglePageLoading();
        });
    },
    async getCurrentConfig() {
      await this.$store
        .dispatch(loadInitialProcessConfig())
        .then((data) => {
          if (data.id) {
            this.saveAction = "update";
          }
          this.initialProcessConfig = data;
          if (this.initialProcessConfig.id) {
            this.processKey = this.initialProcessConfig.processDefinitionId;
            this.xml = this.initialProcessConfig.content;
            this.renderXml();
          }
        })
        .catch((err) => {
          EventBus.$emit("emmit-notification", {
            type: ETypeNotification.DANGER,
            title: this.$t("quiz.startConfig.notifications.loadSelectedProcessError"),
            message: err[0],
          });
        });
    },
    async getAllProcesses() {
      await this.$store
        .dispatch(loadAllProcessesByEnvironment())
        .then((data) => {
          this.processesList = data.sort((a: any, b: any) => a.name.localeCompare(b.name));
        })
        .catch((err) => {
          EventBus.$emit("emmit-notification", {
            type: ETypeNotification.DANGER,
            title: this.$t("quiz.startConfig.notifications.loadProcessListError"),
            message: err[0],
          });
        });
    },
  },
  async mounted() {
    await this.getCurrentConfig();
    await this.getAllProcesses();
    this.togglePageLoading();
  },
});
